import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const shopifyAnalytics: Competitor = {
  "id": "shopify-analytics",
  "name": "Shopify Analytics",
  "group": "ecomm",
  "website": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports",
  "owner": "Shopify Inc. (built-in feature of the Shopify admin; Shopify acqui-hired the Peel Insights analytics team in May 2024)",
  "summary": "Shopify Analytics is the reporting built into every Shopify store: a customizable dashboard, prebuilt reports (sales, customers, profit, marketing, inventory and more), data explorations, the ShopifyQL query editor and Sidekick, Shopify's AI assistant, for plain-English report building. It covers data held in the Shopify store itself and suits merchants whose business runs mostly through Shopify.",
  "bestFor": [
    "Shopify-first brands whose orders, POS and customers mostly live in Shopify and who want reporting at no extra cost",
    "Teams that want quick answers without a data team, using Sidekick to write ShopifyQL from plain-English questions",
    "Shopify Plus organizations that need combined reporting across several Shopify stores"
  ],
  "notFor": [
    "Multi-channel brands that need Amazon Seller Central fees, wholesale, ERP or 3PL data joined to Shopify data",
    "Teams that need the data in their own warehouse to model with dbt or join to finance and ops data",
    "Brands that need full contribution margin including payment fees and ad spend from every platform, or incrementality measurement"
  ],
  "strengths": [
    "Included with every Shopify plan; main analytics features are available on any subscription, so there is no extra license, warehouse or consultant cost",
    "Zero setup: data is native to the store, and Shopify describes the new analytics as real-time, versus pipelines and models to build for a custom warehouse",
    "Prebuilt cohort analysis, predicted spend tier and RFM customer segmentation reports out of the box",
    "Sidekick turns plain-English questions into ShopifyQL reports in the query editor (since Oct 2025), extended to payments and web performance data in Jan 2026",
    "Five attribution models (last non-direct, last click, first click, any click, linear) in marketing reports, with ad spend, ROAS and CAC for ad platforms that send cost data to Shopify",
    "Reporting and ShopifyQL work in the Shopify mobile app, plus home-screen metric widgets",
    "Plus organization analytics combine several stores in one dashboard or ShopifyQL query with currency conversion"
  ],
  "limitations": [
    "Reports read from the store's own database; Shopify's docs do not describe connectors for non-Shopify sources such as Amazon Seller Central, ERPs or 3PLs",
    "No warehouse sync is documented; data leaves through manual report exports (CSV, XML, JSONL, Parquet), and no scheduled export is described",
    "Profit reports need a cost per item recorded and only count items with a cost at time of sale; payment fees and ad spend are not part of the documented profit reports",
    "Ad spend metrics only appear for channels that report cost data to Shopify; channels without it show a dash",
    "Report views display a maximum of 1,000 rows (export to see all) and visualizations show up to 4 metrics at once",
    "Some metric and dimension combinations cannot be used together because they belong to different datasets",
    "Multi-store organization reporting is limited to Shopify Plus"
  ],
  "pricing": {
    "model": "Included in the Shopify subscription (flat monthly plan fee)",
    "publicStartingPrice": "Included at no extra cost; Basic plan is $39/month billed monthly ($29 billed annually) per third-party pricing guide",
    "freeTier": "No separate free tier; analytics comes with any Shopify plan",
    "notes": "Which plans get which reports: Shopify says the main analytics features are available on any plan. Starter (not available to new stores) lists analytics page, finance, product analytics, Live View, and acquisition, inventory, behavior, marketing, order, sales, retail sales, profit and customer reports. Basic includes all reports plus custom reports built with data explorations. Grow includes the same report set and custom reports. Advanced adds what Shopify calls its most comprehensive reports and predicted values for certain reports. Plus adds organization analytics across multiple stores (cross-store dashboards, store dimension, FROM ORGANIZATION ShopifyQL). Plan prices from a third-party guide updated May 2026: Starter $5, Basic $29 annual / $39 monthly, Grow $79 / $105, Advanced $299 / $399, Plus from $2,300/month on a 3-year term. Plan availability of ShopifyQL editor and Sidekick in analytics is not stated in Shopify's docs (unknown). Setup time: none beyond having a store; cohort projections need 24 months of data per cohort.",
    "sourceUrl": "https://help.shopify.com/en/manual/intro-to-shopify/pricing-plans/plans-features/basic-shopify-plan?sh_rd=1",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Native; reports read directly from the store database",
      "sourceUrl": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/reports-overview"
    },
    "amazon": {
      "value": "partial",
      "note": "Marketplace Connect syncs Amazon orders into Shopify; no Seller Central fees/settlement reporting documented",
      "sourceUrl": "https://changelog.shopify.com/posts/shopify-marketplace-connect-sell-your-shopify-products-on-top-online-marketplaces",
      "badge": "Orders only, no fees"
    },
    "tiktok_shop": {
      "value": "unknown",
      "note": "Not covered in the analytics docs reviewed",
      "sourceUrl": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/analytics-data"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "Shopify POS retail sales reports are native; no ERP, 3PL or wholesale-platform connectors documented",
      "sourceUrl": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/default-reports"
    },
    "ad_platforms": {
      "value": "partial",
      "note": "Ad spend, ROAS, CAC only for channels that report cost data to Shopify via connected apps",
      "sourceUrl": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/marketing-reports"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Five click-based attribution models on Shopify's own session data; no view-through or cross-device MTA documented",
      "sourceUrl": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/marketing-reports"
    },
    "mmm_incrementality": {
      "value": "no",
      "note": "No MMM or incrementality testing in Shopify analytics docs",
      "sourceUrl": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/marketing-reports"
    },
    "profit_cogs": {
      "value": "partial",
      "note": "Gross profit by product/variant/POS and margin reports from cost per item; fees and ad spend not included",
      "sourceUrl": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/profit-reports?ld=1"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "Customer cohort analysis, predicted spend tier, RFM analysis",
      "sourceUrl": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/customers-reports?r=ecm-bfs"
    },
    "subscription_analytics": {
      "value": "unknown",
      "note": "No Recharge or subscription-specific reports in docs reviewed",
      "sourceUrl": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/default-reports"
    },
    "warehouse_access": {
      "value": "no",
      "note": "No warehouse sync documented; manual exports in CSV, XML, JSONL or Parquet",
      "sourceUrl": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/custom-reports/export-reports"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Custom reports and explorations over Shopify's metrics; custom formula metrics not documented",
      "sourceUrl": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/custom-reports"
    },
    "custom_sql": {
      "value": "partial",
      "note": "ShopifyQL, a commerce query language, in the query editor; not standard SQL and store data only",
      "sourceUrl": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/shopifyql-editor",
      "badge": "ShopifyQL"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Sidekick builds reports from plain-English questions in the query editor",
      "sourceUrl": "https://changelog.shopify.com/posts/turn-business-questions-into-analytics-reports-with-natural-language-queries"
    },
    "mobile_app": {
      "value": "yes",
      "note": "Reporting and ShopifyQL in the Shopify mobile app; analytics widgets",
      "sourceUrl": "https://changelog.shopify.com/posts/new-analytics-is-now-the-default"
    }
  },
  "recentChanges": [
    {
      "date": "2026-01",
      "change": "Sidekick can query payments and web performance data and generate ShopifyQL for it",
      "sourceUrl": "https://changelog.shopify.com/posts/payments-and-web-performance-data-available-in-sidekick"
    },
    {
      "date": "2025-10",
      "change": "Sidekick built into the analytics query editor to create reports from natural-language questions",
      "sourceUrl": "https://changelog.shopify.com/posts/turn-business-questions-into-analytics-reports-with-natural-language-queries"
    },
    {
      "date": "2025-06",
      "change": "Redesigned analytics made the default for all stores: real-time data, custom dashboards, explorations, in-context ShopifyQL, mobile support",
      "sourceUrl": "https://changelog.shopify.com/posts/new-analytics-is-now-the-default"
    },
    {
      "date": "2025-06",
      "change": "Live View, benchmarks and customer cohort analysis brought into the new analytics experience",
      "sourceUrl": "https://changelog.shopify.com/posts/live-view-benchmarks-and-customer-cohort-analysis-are-now-supported-in-the-new-analytics-experience"
    },
    {
      "date": "2024-05",
      "change": "Shopify acqui-hired the Peel Insights analytics team",
      "sourceUrl": "https://ground.news/article/shopify-has-acqui-hired-ai-driven-analytics-startup-peel-insights"
    }
  ],
  "sources": [
    {
      "title": "Shopify Help: Shopify reports and analytics",
      "url": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports"
    },
    {
      "title": "Shopify Help: Shopify analytics data",
      "url": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/analytics-data"
    },
    {
      "title": "Shopify Help: Overview of the new Shopify reports",
      "url": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/reports-overview"
    },
    {
      "title": "Shopify Help: Types of reports",
      "url": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types"
    },
    {
      "title": "Shopify Help: Default Shopify reports",
      "url": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/default-reports"
    },
    {
      "title": "Shopify Help: Custom reports",
      "url": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/custom-reports"
    },
    {
      "title": "Shopify Help: Exporting reports",
      "url": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/custom-reports/export-reports"
    },
    {
      "title": "Shopify Help: ShopifyQL editor",
      "url": "https://help.shopify.com/en/manual/reports-and-analytics/shopify-reports/report-types/shopifyql-editor"
    },
    {
      "title": "Shopify Help: Customers reports",
      "url": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/customers-reports?r=ecm-bfs"
    },
    {
      "title": "Shopify Help: Profit reports",
      "url": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/profit-reports?ld=1"
    },
    {
      "title": "Shopify Help: Marketing reports",
      "url": "https://help.shopify.com/manual/reports-and-analytics/shopify-reports/report-types/marketing-reports"
    },
    {
      "title": "Shopify Help: Organization analytics (Plus)",
      "url": "https://help.shopify.com/en/manual/organization-settings/analytics"
    },
    {
      "title": "Shopify Help: Starter plan",
      "url": "https://help.shopify.com/en/manual/intro-to-shopify/pricing-plans/plans-features/shopify-starter-plan?term=SEO"
    },
    {
      "title": "Shopify Help: Basic plan",
      "url": "https://help.shopify.com/en/manual/intro-to-shopify/pricing-plans/plans-features/basic-shopify-plan?sh_rd=1"
    },
    {
      "title": "Shopify Help: Grow plan",
      "url": "https://help.shopify.com/en/manual/intro-to-shopify/pricing-plans/plans-features/shopify-plan?sh_rd=1"
    },
    {
      "title": "Shopify Help: Advanced plan",
      "url": "https://help.shopify.com/manual/intro-to-shopify/pricing-plans/plans-features/shopify-advanced-plan"
    },
    {
      "title": "Shopify Help: Using Shopify mobile widgets",
      "url": "https://help.shopify.com/en/manual/shopify-admin/shopify-app/using-shopify-mobile-widgets?r_done=1"
    },
    {
      "title": "Shopify Changelog: New analytics is now the default",
      "url": "https://changelog.shopify.com/posts/new-analytics-is-now-the-default"
    },
    {
      "title": "Shopify Changelog: Natural language queries in analytics",
      "url": "https://changelog.shopify.com/posts/turn-business-questions-into-analytics-reports-with-natural-language-queries"
    },
    {
      "title": "Shopify Changelog: Payments and web performance data in Sidekick",
      "url": "https://changelog.shopify.com/posts/payments-and-web-performance-data-available-in-sidekick"
    },
    {
      "title": "Shopify Changelog: Cohort analysis in new analytics",
      "url": "https://changelog.shopify.com/posts/live-view-benchmarks-and-customer-cohort-analysis-are-now-supported-in-the-new-analytics-experience"
    },
    {
      "title": "Shopify Changelog: Marketplace Connect",
      "url": "https://changelog.shopify.com/posts/shopify-marketplace-connect-sell-your-shopify-products-on-top-online-marketplaces"
    },
    {
      "title": "Website Builder Expert: Shopify Pricing 2026 (third-party, plan prices)",
      "url": "https://www.websitebuilderexpert.com/ecommerce-website-builders/shopify/shopify-pricing"
    }
  ],
  "lastReviewed": "2026-11-03"
};
